import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  CircularProgress,
  MenuItem,
  Paper,
  Stack,
  Tabs,
  Tab,
  TextField,
  Typography,
  Divider,
} from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';

import PlayerRankingWidget from '../../components/stats/PlayerRankingWidget';
import TeamRankingWidget from '../../components/stats/TeamRankingWidget';
import StandingsWidget from '../../components/stats/StandingsWidget';
import ResultsWidget from '../../components/stats/ResultsWidget';

import {
  getCompetitionsForRankingBuilder,
  getCompetitionSeasonsForRankingBuilder,
  getRankingCompetitionMeta,
  getWidgetConfiguration,
} from '../../../api';
import { getSingleCompetitionName, resolveWidgetDisplayTitle } from '../../utils/widgetDisplayTitle';
import DirectCalendarWidget from '../../components/direct/DirectCalendarWidget';
import './StatsBuilderLayout.css';
import Footer from '../footer/Footer';

const DISCIPLINE_LABELS = {
  rugby: 'Rugby',
  soccer: 'Football',
};

const DISCIPLINE_OPTIONS = [
  { value: 'rugby', label: 'Rugby' },
  { value: 'soccer', label: 'Football' },
];

const TAB_LABELS = {
  player_ranking: 'Statistiques joueurs',
  team_ranking: 'Statistiques équipes',
  team_standings: 'Classement',
  competition_results: 'Résultats',
  direct_calendar: 'Directs',
};

const getSeasonDisplayLabel = (seasonValue) => {
  const value = String(seasonValue || '').trim();
  if (!value) return '';

  if (value.includes('/')) {
    return value.split('/')[0];
  }

  return value;
};

const normalizeSeasonOptions = (rawSeasons = []) => {
  const byLabel = new Map();

  rawSeasons.forEach((seasonValue) => {
    const value = String(seasonValue || '').trim();
    if (!value) return;

    const label = getSeasonDisplayLabel(value);
    const existing = byLabel.get(label);

    if (!existing) {
      byLabel.set(label, { value, label });
      return;
    }

    if (!existing.value.includes('/') && value.includes('/')) {
      byLabel.set(label, { value, label });
    }
  });

  return Array.from(byLabel.values()).sort((a, b) =>
    b.label.localeCompare(a.label)
  );
};

        const getSeasonStartYear = (seasonValue) => {
          const value = String(seasonValue || '').trim();
          if (!value) return null;

          const match = value.match(/^(\d{4})(?:\/\d{4})?$/);
          if (!match) return null;

          return Number(match[1]);
        };

        const isSeasonAllowedBySince = (seasonValue, seasonSince) => {
          const sinceYear = Number(String(seasonSince || '').trim());
          if (!Number.isFinite(sinceYear)) return true;

          const seasonStartYear = getSeasonStartYear(seasonValue);
          if (!Number.isFinite(seasonStartYear)) return false;

          return seasonStartYear >= sinceYear;
        };

        const getSeasonValues = (options = []) =>
          options.map((option) => option.value).filter(Boolean);

        const applySeasonParamsToSearchParams = ({
  nextParams,
  selectedSeasonOptions = [],
  seasonMode,
  configuredSeasonSince,
}) => {
  const selectedSeasonValues = getSeasonValues(selectedSeasonOptions);

  if (selectedSeasonValues.length) {
    nextParams.set('season', selectedSeasonValues.join(','));
    nextParams.delete('seasonSince');
    return;
  }

  nextParams.delete('season');

  if (seasonMode === 'since' && configuredSeasonSince) {
    nextParams.set('seasonSince', configuredSeasonSince);
  } else {
    nextParams.delete('seasonSince');
  }
};


const parsePositiveDay = (value) => {
  if (value == null || value === '') return '';
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed < 1) return '';
  return String(Math.floor(parsed));
};

const normalizeDayValue = (value) => {
  if (value == null) return null;
  const normalized = String(value).trim();
  return normalized !== '' ? normalized : null;
};

const MAX_SELECTED_COMPETITIONS = 5;

const clampCompetitionIds = (value = '') =>
  String(value || '')
    .split(',')
    .map((id) => id.trim())
    .filter(Boolean)
    .slice(0, MAX_SELECTED_COMPETITIONS);

const clampCompetitionObjects = (value = []) =>
  (Array.isArray(value) ? value : []).slice(0, MAX_SELECTED_COMPETITIONS);

export default function ComposedTabsBuilderPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [widgetConfig, setWidgetConfig] = useState(null);
  const [widgetConfigLoading, setWidgetConfigLoading] = useState(false);
  const [widgetConfigError, setWidgetConfigError] = useState('');
  const [calendarReloadKey, setCalendarReloadKey] = useState(0);
  const [calendarCompetitionFilterPortalTarget, setCalendarCompetitionFilterPortalTarget] = useState(null);
  const [showCalendarCompetitionFilter, setShowCalendarCompetitionFilter] = useState(false);

  const discipline = searchParams.get('discipline') || '';
  const seasonParam = searchParams.get('season') || '';
  const seasonSinceParam = searchParams.get('seasonSince') || '';
  const competitionIdsParam = searchParams.get('competitionIds') || '';
  const calendarCompetitionIdsParam = searchParams.get('calendarCompetitionIds') || '';
  const dayParam = searchParams.get('day') || '';
  const groupKeyParam = searchParams.get('groupKey') || '';
  const tabParam = searchParams.get('tab') || '';
  const widgetConfigurationId = searchParams.get('widgetConfigurationId') || '';
  const statType = searchParams.get('statType') || '';

  const [seasonOptions, setSeasonOptions] = useState([]);
  const [selectedSeasons, setSelectedSeasons] = useState([]);
  const [loadingSeasons, setLoadingSeasons] = useState(false);

  const [competitions, setCompetitions] = useState([]);
  const [selectedCompetitions, setSelectedCompetitions] = useState([]);
  const [loadingCompetitions, setLoadingCompetitions] = useState(false);
  const [competitionsError, setCompetitionsError] = useState('');

  const [competitionInfos, setCompetitionInfos] = useState([]);
  const [metaLoading, setMetaLoading] = useState(false);
  const [maxDay, setMaxDay] = useState(null);
  const [dayInput, setDayInput] = useState(parsePositiveDay(dayParam));

  const seasonClearedManuallyRef = useRef(false);
  const competitionClearedManuallyRef = useRef(false);

  const replaceSearchParamsIfChanged = (nextParams) => {
    const current = searchParams.toString();
    const next = nextParams.toString();

    if (next !== current) {
      setSearchParams(nextParams, { replace: true });
    }
  };

  const handleCalendarCompetitionFilterVisibilityChange = useCallback((visible) => {
    setShowCalendarCompetitionFilter(Boolean(visible));
  }, []);

  const competitionIds = useMemo(
  () => clampCompetitionIds(competitionIdsParam),
  [competitionIdsParam]
);

const selectedCompetitionIds = useMemo(
  () => clampCompetitionIds(competitionIdsParam),
  [competitionIdsParam]
);

const selectedCalendarCompetitionIds = useMemo(
  () => clampCompetitionIds(calendarCompetitionIdsParam),
  [calendarCompetitionIdsParam]
);

  const selectedSeasonsFromUrl = useMemo(
    () =>
      seasonParam
        .split(',')
        .map((value) => value.trim())
        .filter(Boolean),
    [seasonParam]
  );

  const widgetSeason =
    selectedSeasonsFromUrl.length > 0
      ? selectedSeasonsFromUrl.join(',')
      : null;

  const childWidgets = useMemo(() => {
    if (Array.isArray(widgetConfig?.childWidgets) && widgetConfig.childWidgets.length) {
      return [...widgetConfig.childWidgets].sort(
        (a, b) => Number(a?.order || 0) - Number(b?.order || 0)
      );
    }

    if (Array.isArray(widgetConfig?.childWidgetTypes) && widgetConfig.childWidgetTypes.length) {
     
      return widgetConfig.childWidgetTypes.map((widgetType, index) => ({
        widgetType,
        label: TAB_LABELS[widgetType] || widgetType,
        order: index,
        widgetConfigurationId: null,
      }));
    }

    return [];
  }, [widgetConfig]);

  const availableTabValues = useMemo(
    () => childWidgets.map((child) => child.widgetType).filter(Boolean),
    [childWidgets]
  );

  const activeTab = useMemo(() => {
    if (tabParam && availableTabValues.includes(tabParam)) {
      return tabParam;
    }
    return availableTabValues[0] || '';
  }, [tabParam, availableTabValues]);

  const activeChildWidget = useMemo(
    () => childWidgets.find((child) => child.widgetType === activeTab) || null,
    [childWidgets, activeTab]
  );

  const activeWidgetConfigurationId =
    activeChildWidget?.widgetConfigurationId
      ? String(activeChildWidget.widgetConfigurationId)
      : null;

  const activeTabIsDirectCalendar = activeTab === 'direct_calendar';

  const tabUsesSharedSeasonCompetitionFilters = (tab) =>
  tab !== 'direct_calendar';

  const tabUsesCompetitionFilters = (tab) =>
    tab !== 'direct_calendar';

  const tabUsesDayFilter = (tab) =>
    tab === 'team_standings';

  const allowedDisciplines = useMemo(() => {
    if (Array.isArray(widgetConfig?.disciplines) && widgetConfig.disciplines.length) {
      return widgetConfig.disciplines;
    }
    if (widgetConfig?.discipline) {
      return [widgetConfig.discipline];
    }
    return [];
  }, [widgetConfig]);

  const effectiveProvider = widgetConfig?.provider || '';

const effectiveDiscipline = useMemo(() => {
  if (!allowedDisciplines.length) {
    return discipline || '';
  }

  if (discipline && allowedDisciplines.includes(discipline)) {
    return discipline;
  }

  if (
    widgetConfig?.defaultDiscipline &&
    allowedDisciplines.includes(widgetConfig.defaultDiscipline)
  ) {
    return widgetConfig.defaultDiscipline;
  }

  if (allowedDisciplines.length === 1) {
    return allowedDisciplines[0];
  }

  return '';
}, [allowedDisciplines, discipline, widgetConfig]);

const waitingForDisciplineSelection =
  allowedDisciplines.length > 1 && !effectiveDiscipline && !activeTabIsDirectCalendar;

  const disciplineConfig = useMemo(() => {
  return widgetConfig?.filtersByDiscipline?.[effectiveDiscipline] || {};
}, [widgetConfig, effectiveDiscipline]);

    const seasonMode = disciplineConfig?.seasonMode === 'since' ? 'since' : 'explicit';

    const configuredSeasonSince = useMemo(() => {
    return seasonMode === 'since' && disciplineConfig?.seasonSince
        ? String(disciplineConfig.seasonSince).trim()
        : '';
    }, [disciplineConfig, seasonMode]);

    const allowedSeasonValues = useMemo(
    () => new Set((disciplineConfig?.allowedSeasons || []).map((v) => String(v).trim()).filter(Boolean)),
    [disciplineConfig]
    );

    const defaultSeasonValues = useMemo(
    () => (disciplineConfig?.defaultSeasons || []).map((v) => String(v).trim()).filter(Boolean),
    [disciplineConfig]
    );

    const allowedCompetitionIds = useMemo(
    () => new Set((disciplineConfig?.allowedCompetitionIds || []).map((v) => String(v).trim()).filter(Boolean)),
    [disciplineConfig]
    );

    const defaultCompetitionIds = useMemo(
    () => (disciplineConfig?.defaultCompetitionIds || []).map((v) => String(v).trim()).filter(Boolean),
    [disciplineConfig]
    );

    const composedFallbackTitle = useMemo(() => {
        return getSingleCompetitionName(competitionInfos);
      }, [competitionInfos]);

      const resolvedComposedTitle = useMemo(
        () =>
          resolveWidgetDisplayTitle({
            widgetConfig,
            fallbackTitle: composedFallbackTitle,
          }),
        [widgetConfig, composedFallbackTitle]
      );

  const isMultiCompetition = selectedCompetitionIds.length > 1;
  const showDayFilter = activeTab === 'team_standings';

  useEffect(() => {
    setDayInput(parsePositiveDay(dayParam));
  }, [dayParam]);

  useEffect(() => {
    let ignore = false;

    const loadWidgetConfig = async () => {
      if (!widgetConfigurationId) {
        if (!ignore) {
          setWidgetConfig(null);
          setWidgetConfigError('widgetConfigurationId est requis.');
        }
        return;
      }

      try {
        setWidgetConfigLoading(true);
        setWidgetConfigError('');

        const { data } = await getWidgetConfiguration(widgetConfigurationId);

        if (ignore) return;

        if (!data) {
          setWidgetConfig(null);
          setWidgetConfigError('Configuration widget introuvable.');
          return;
        }

        if (data.widgetType !== 'composed_tabs') {
          setWidgetConfig(null);
          setWidgetConfigError("Cette configuration n'est pas un widget composé.");
          return;
        }

        setWidgetConfig(data);
      } catch (e) {
        if (!ignore) {
          setWidgetConfig(null);
          setWidgetConfigError('Impossible de charger la configuration du widget composé.');
        }
      } finally {
        if (!ignore) {
          setWidgetConfigLoading(false);
        }
      }
    };

    loadWidgetConfig();

    return () => {
      ignore = true;
    };
  }, [widgetConfigurationId]);

  useEffect(() => {
    if (!activeTab) return;
    if (tabParam === activeTab) return;

    const nextParams = new URLSearchParams(searchParams);
    nextParams.set('tab', activeTab);
    replaceSearchParamsIfChanged(nextParams);
  }, [activeTab, tabParam, searchParams, setSearchParams]);

  useEffect(() => {
    let ignore = false;

    const loadSeasons = async () => {

      if (activeTabIsDirectCalendar) {
        setSeasonOptions([]);
        setSelectedSeasons([]);
        return;
      }

      if (!effectiveProvider || !effectiveDiscipline) {
        setSeasonOptions([]);
        setSelectedSeasons([]);
        return;
      }

      try {
        setLoadingSeasons(true);

        const { data } = await getCompetitionSeasonsForRankingBuilder({
          provider: effectiveProvider,
          discipline: effectiveDiscipline,
        });

        if (ignore) return;

        const rawSeasons = Array.isArray(data) ? data : [];

        const normalizedSeasons = normalizeSeasonOptions(rawSeasons).filter((option) => {
          if (seasonMode === 'since') {
            return isSeasonAllowedBySince(option.value, configuredSeasonSince);
          }

          return allowedSeasonValues.size === 0 || allowedSeasonValues.has(option.value);
        });

       setSeasonOptions(normalizedSeasons);

        const preselectedFromUrl = normalizedSeasons.filter((option) =>
          selectedSeasonsFromUrl.includes(option.value)
        );

        if (preselectedFromUrl.length) {
          setSelectedSeasons(preselectedFromUrl);

          if (preselectedFromUrl.length !== selectedSeasonsFromUrl.length) {
            const nextParams = new URLSearchParams(searchParams);

            applySeasonParamsToSearchParams({
              nextParams,
              selectedSeasonOptions: preselectedFromUrl,
              seasonMode,
              configuredSeasonSince,
            });

            if (effectiveDiscipline) {
              nextParams.set('discipline', effectiveDiscipline);
            }

            if (widgetConfigurationId) {
              nextParams.set('widgetConfigurationId', widgetConfigurationId);
            }

            // uniquement dans ComposedTabsBuilderPage
            if (typeof activeTab !== 'undefined' && activeTab) {
              nextParams.set('tab', activeTab);
            }

            replaceSearchParamsIfChanged
              ? replaceSearchParamsIfChanged(nextParams)
              : setSearchParams(nextParams, { replace: true });
          }
        } else if (normalizedSeasons.length && !seasonClearedManuallyRef.current) {
          const configuredDefaults = normalizedSeasons.filter((option) =>
            defaultSeasonValues.includes(option.value)
          );

          const nextSelected =
          configuredDefaults.length
            ? configuredDefaults
            : [normalizedSeasons[0]];

          setSelectedSeasons(nextSelected);

          const nextParams = new URLSearchParams(searchParams);

          applySeasonParamsToSearchParams({
            nextParams,
            selectedSeasonOptions: nextSelected,
            seasonMode,
            configuredSeasonSince,
          });

          if (effectiveDiscipline) {
            nextParams.set('discipline', effectiveDiscipline);
          }

          if (widgetConfigurationId) {
            nextParams.set('widgetConfigurationId', widgetConfigurationId);
          }

          // uniquement dans ComposedTabsBuilderPage
          if (typeof activeTab !== 'undefined' && activeTab) {
            nextParams.set('tab', activeTab);
          }

          replaceSearchParamsIfChanged
            ? replaceSearchParamsIfChanged(nextParams)
            : setSearchParams(nextParams, { replace: true });
        } else {
          setSelectedSeasons([]);
        }


      } catch (error) {
        if (!ignore) {
          setSeasonOptions([]);
          setSelectedSeasons([]);
        }
      } finally {
        if (!ignore) {
          setLoadingSeasons(false);
        }
      }
    };

    loadSeasons();

    return () => {
      ignore = true;
    };
  },  [
  effectiveProvider,
  effectiveDiscipline,
  selectedSeasonsFromUrl,
  seasonSinceParam,
  widgetConfigurationId,
  activeTab,
  seasonMode,
  configuredSeasonSince,
  allowedSeasonValues,
  defaultSeasonValues,
]);

  useEffect(() => {
    let ignore = false;

    const loadCompetitions = async () => {

      if (activeTabIsDirectCalendar) {
        setCompetitions([]);
        setSelectedCompetitions([]);
        setCompetitionInfos([]);
        setMaxDay(null);
        return;
      }

      if (!effectiveProvider || !effectiveDiscipline) {
        setCompetitions([]);
        setSelectedCompetitions([]);
        return;
      }

      try {
        setLoadingCompetitions(true);
        setCompetitionsError('');
        const selectedSeasonValues = getSeasonValues(selectedSeasons);

        const shouldUseSeasonSince =
          seasonMode === 'since' &&
          configuredSeasonSince &&
          selectedSeasonValues.length === 0;

        const { data } = await getCompetitionsForRankingBuilder({
          provider: effectiveProvider,
          discipline: effectiveDiscipline,
          season: shouldUseSeasonSince
            ? undefined
            : selectedSeasonValues.length
            ? selectedSeasonValues.join(',')
            : undefined,
          seasonSince: shouldUseSeasonSince ? configuredSeasonSince : undefined,
        });

        if (ignore) return;

        const normalized = (Array.isArray(data) ? data : []).filter((competition) =>
            allowedCompetitionIds.size === 0 ||
            allowedCompetitionIds.has(String(competition._id))
            );
        setCompetitions(normalized);

        const preselected = normalized
        .filter((competition) =>
          selectedCompetitionIds.includes(String(competition._id))
        )
        .slice(0, MAX_SELECTED_COMPETITIONS);

      setSelectedCompetitions(preselected);

      const defaultCompetitions = normalized
        .filter((competition) =>
          defaultCompetitionIds.includes(String(competition._id))
        )
        .slice(0, MAX_SELECTED_COMPETITIONS);

            const fallbackCompetitions =
              normalized.length === 1 ? [normalized[0]] : [];

            const nextCompetitions =
              defaultCompetitions.length ? defaultCompetitions : fallbackCompetitions;

            if (selectedCompetitionIds.length && preselected.length !== selectedCompetitionIds.length) {
              const nextParams = new URLSearchParams(searchParams);

              if (effectiveDiscipline) nextParams.set('discipline', effectiveDiscipline);
              if (widgetConfigurationId) nextParams.set('widgetConfigurationId', widgetConfigurationId);
              if (activeTab) nextParams.set('tab', activeTab);

              applySeasonParamsToSearchParams({
                nextParams,
                selectedSeasonOptions: selectedSeasons,
                seasonMode,
                configuredSeasonSince,
              });

              if (preselected.length) {
                nextParams.set(
                  'competitionIds',
                  preselected.map((competition) => String(competition._id)).join(',')
                );
              } else if (nextCompetitions.length) {
                setSelectedCompetitions(nextCompetitions);

                nextParams.set(
                  'competitionIds',
                  nextCompetitions.map((competition) => String(competition._id)).join(',')
                );
              } else {
                nextParams.delete('competitionIds');
              }

              replaceSearchParamsIfChanged(nextParams);
           } else if (
                !selectedCompetitionIds.length &&
                nextCompetitions.length &&
                !competitionClearedManuallyRef.current
              ) {
              setSelectedCompetitions(nextCompetitions);

              const nextParams = new URLSearchParams(searchParams);
              if (effectiveDiscipline) nextParams.set('discipline', effectiveDiscipline);
              if (widgetConfigurationId) nextParams.set('widgetConfigurationId', widgetConfigurationId);
              if (activeTab) nextParams.set('tab', activeTab);

              applySeasonParamsToSearchParams({
                nextParams,
                selectedSeasonOptions: selectedSeasons,
                seasonMode,
                configuredSeasonSince,
              });

              nextParams.set(
                'competitionIds',
                nextCompetitions.map((competition) => String(competition._id)).join(',')
              );

              replaceSearchParamsIfChanged(nextParams);
            }
      } catch (error) {
        if (!ignore) {
          setCompetitions([]);
          setSelectedCompetitions([]);
          setCompetitionsError('Impossible de charger les compétitions.');
        }
      } finally {
        if (!ignore) {
          setLoadingCompetitions(false);
        }
      }
    };

    loadCompetitions();

    return () => {
      ignore = true;
    };
  }, [
  effectiveProvider,
  effectiveDiscipline,
  selectedSeasons,
  seasonSinceParam,
  selectedCompetitionIds,
  allowedCompetitionIds,
  defaultCompetitionIds,
  seasonMode,
  configuredSeasonSince,
  widgetConfigurationId,
  activeTab,
]);

  useEffect(() => {
    let ignore = false;

    const loadMeta = async () => {
      const firstCompetitionId = selectedCompetitionIds[0];

      if (!ignore) {
        setCompetitionInfos([]);
      }

      if (!firstCompetitionId) {
        if (!ignore) {
          setCompetitionInfos([]);
          setMetaLoading(false);
          setMaxDay(null);
        }
        return;
      }

      try {
        setMetaLoading(true);

        const metas = await Promise.all(
          selectedCompetitionIds.map(async (competitionId) => {
            const { data } = await getRankingCompetitionMeta(competitionId);
            return data;
          })
        );

        if (ignore) return;

        const normalizedInfos = metas
          .map((meta, index) => ({
            id: selectedCompetitionIds[index],
            name: meta?.name || '',
            season: meta?.season || '',
            nbDays: meta?.nbDays ?? null,
            defaultDay: meta?.defaultDay ?? null,
            rounds: Array.isArray(meta?.rounds) ? meta.rounds : [],
            rankingMode: meta?.rankingMode || null,
            groups: Array.isArray(meta?.groups) ? meta.groups : [],
            thirdPartyId: meta?.thirdPartyId || null,
            thirdPartyEntityId: meta?.thirdPartyEntityId || null,
          }))
          .filter((item) => item.id);

        setCompetitionInfos(normalizedInfos);

        if (selectedCompetitionIds.length === 1 && activeTab === 'team_standings') {
          const nbDays = Number(normalizedInfos?.[0]?.nbDays);
          const normalizedMaxDay =
            Number.isFinite(nbDays) && nbDays > 0 ? nbDays : null;

          const normalizedDefaultDay = parsePositiveDay(normalizedInfos?.[0]?.defaultDay);

          setMaxDay(normalizedMaxDay);

          const parsedDay = parsePositiveDay(dayParam);

          if (!parsedDay) {
            const fallbackDay =
              normalizedDefaultDay || (normalizedMaxDay ? String(normalizedMaxDay) : '');

            if (fallbackDay) {
              const nextParams = new URLSearchParams(searchParams);
              nextParams.set('day', fallbackDay);
              if (widgetConfigurationId) {
                nextParams.set('widgetConfigurationId', widgetConfigurationId);
              }
              if (activeTab) {
                nextParams.set('tab', activeTab);
              }
              replaceSearchParamsIfChanged(nextParams);
            }
          } else if (
            normalizedMaxDay &&
            Number(parsedDay) > normalizedMaxDay
          ) {
            const nextParams = new URLSearchParams(searchParams);
            nextParams.set('day', String(normalizedMaxDay));
            if (widgetConfigurationId) {
              nextParams.set('widgetConfigurationId', widgetConfigurationId);
            }
            if (activeTab) {
              nextParams.set('tab', activeTab);
            }
            replaceSearchParamsIfChanged(nextParams);
          }
        } else {
          setMaxDay(null);
        }
      } catch (error) {
        if (!ignore) {
          setCompetitionInfos([]);
          setMaxDay(null);
        }
      } finally {
        if (!ignore) {
          setMetaLoading(false);
        }
      }
    };

    loadMeta();

    return () => {
      ignore = true;
    };
  }, [
    selectedCompetitionIds,
    activeTab,
    widgetConfigurationId,
  ]);

  useEffect(() => {
  seasonClearedManuallyRef.current = false;
}, [
  effectiveProvider,
  effectiveDiscipline,
  seasonMode,
  configuredSeasonSince,
  widgetConfigurationId,
  activeTab,
]);

  useEffect(() => {
    competitionClearedManuallyRef.current = false;
  }, [
    effectiveProvider,
    effectiveDiscipline,
    seasonMode,
    configuredSeasonSince,
    selectedSeasons.map((option) => option.value).join(','),
    widgetConfigurationId,
    activeTab,
  ]);

  const handleDisciplineChange = (event) => {
    const value = event.target.value;

    const nextParams = new URLSearchParams();
    // if (effectiveProvider) nextParams.set('provider', effectiveProvider);
    if (value) nextParams.set('discipline', value);
    if (widgetConfigurationId) nextParams.set('widgetConfigurationId', widgetConfigurationId);
    if (activeTab) nextParams.set('tab', activeTab);

    setSelectedSeasons([]);
    setSelectedCompetitions([]);
    setCompetitionInfos([]);
    setDayInput('');
    setMaxDay(null);

    replaceSearchParamsIfChanged(nextParams);
  };

  const handleSeasonsChange = (_, value) => {
    setSelectedSeasons(value);
    setSelectedCompetitions([]);
    setCompetitionInfos([]);
    setDayInput('');
    setMaxDay(null);
    seasonClearedManuallyRef.current = Array.isArray(value) && value.length === 0;
    competitionClearedManuallyRef.current = false;

    const nextParams = new URLSearchParams();
    // if (effectiveProvider) nextParams.set('provider', effectiveProvider);
    if (effectiveDiscipline) nextParams.set('discipline', effectiveDiscipline);
    if (widgetConfigurationId) nextParams.set('widgetConfigurationId', widgetConfigurationId);

    applySeasonParamsToSearchParams({
      nextParams,
      selectedSeasonOptions: value,
      seasonMode,
      configuredSeasonSince,
    });

    if (activeTab) {
      nextParams.set('tab', activeTab);
    }

    replaceSearchParamsIfChanged(nextParams);
  };

const handleCompetitionsChange = (_, value) => {
  const nextValue = clampCompetitionObjects(value);
  setSelectedCompetitions(nextValue);

  competitionClearedManuallyRef.current = nextValue.length === 0;

  const nextParams = new URLSearchParams();
  if (effectiveDiscipline) nextParams.set('discipline', effectiveDiscipline);

  applySeasonParamsToSearchParams({
    nextParams,
    selectedSeasonOptions: selectedSeasons,
    seasonMode,
    configuredSeasonSince,
  });

  if (nextValue.length) {
    nextParams.set(
      'competitionIds',
      nextValue.map((competition) => String(competition._id)).join(',')
    );
  }

  if (widgetConfigurationId) nextParams.set('widgetConfigurationId', widgetConfigurationId);
  if (activeTab) nextParams.set('tab', activeTab);

  replaceSearchParamsIfChanged(nextParams);
};

const handleCalendarCompetitionIdsChange = (nextCompetitionIds = []) => {
  const nextParams = new URLSearchParams(searchParams);

  nextParams.delete('date');
  nextParams.delete('season');
  nextParams.delete('seasonSince');
  nextParams.delete('competitionIds');
  nextParams.delete('day');
  nextParams.delete('statType');

  if (effectiveDiscipline) {
    nextParams.set('discipline', effectiveDiscipline);
  }

  if (widgetConfigurationId) {
    nextParams.set('widgetConfigurationId', widgetConfigurationId);
  }

  nextParams.set('tab', 'direct_calendar');

  if (Array.isArray(nextCompetitionIds) && nextCompetitionIds.length) {
    nextParams.set('calendarCompetitionIds', nextCompetitionIds.join(','));
  } else {
    nextParams.delete('calendarCompetitionIds');
  }

  replaceSearchParamsIfChanged(nextParams);
};

const handleTabChange = (_, nextTab) => {
  const nextParams = new URLSearchParams(searchParams);

  nextParams.set('tab', nextTab);

  nextParams.delete('day');
  nextParams.delete('statType');

  if (nextTab === 'direct_calendar') {
    nextParams.delete('season');
    nextParams.delete('seasonSince');
    nextParams.delete('competitionIds');
    nextParams.delete('day');
    nextParams.delete('statType');

    if (calendarCompetitionIdsParam) {
      nextParams.set('calendarCompetitionIds', calendarCompetitionIdsParam);
    }

  } else {

    nextParams.delete('calendarCompetitionIds');

    if (effectiveDiscipline) {
      nextParams.set('discipline', effectiveDiscipline);
    }

    if (widgetConfigurationId) {
      nextParams.set('widgetConfigurationId', widgetConfigurationId);
    }

    applySeasonParamsToSearchParams({
      nextParams,
      selectedSeasonOptions: selectedSeasons,
      seasonMode,
      configuredSeasonSince,
    });

    if (selectedCompetitions.length) {
      nextParams.set(
        'competitionIds',
        selectedCompetitions
          .map((competition) => String(competition._id))
          .join(',')
      );
    }
  }

  replaceSearchParamsIfChanged(nextParams);
};

  const handleStatTypeChange = (nextStatType, options = {}) => {
  const nextParams = new URLSearchParams(searchParams);

  if (options.isDefaultTab) {
    nextParams.delete('statType');
  } else {
    nextParams.set('statType', nextStatType);
  }

  if (widgetConfigurationId) {
    nextParams.set('widgetConfigurationId', widgetConfigurationId);
  }

  if (activeTab) {
    nextParams.set('tab', activeTab);
  }

  replaceSearchParamsIfChanged(nextParams);
};

  const handleDayChange = (event) => {
    const value = event.target.value.replace(/[^\d]/g, '');
    setDayInput(value);
  };

  const handleDayBlur = () => {
    const normalized = parsePositiveDay(dayInput);
    const nextParams = new URLSearchParams(searchParams);

    if (!normalized) {
      nextParams.delete('day');
      replaceSearchParamsIfChanged(nextParams);
      return;
    }

    let nextDay = Number(normalized);
    if (maxDay && nextDay > maxDay) {
      nextDay = maxDay;
    }

    nextParams.set('day', String(nextDay));
    replaceSearchParamsIfChanged(nextParams);
  };

  const handleDaySelectChange = (event) => {
    const value = parsePositiveDay(event.target.value);
    const nextParams = new URLSearchParams(searchParams);

    if (!value) {
      nextParams.delete('day');
    } else {
      nextParams.set('day', value);
    }

    replaceSearchParamsIfChanged(nextParams);
  };

  const handleRefreshCalendar = () => {
    setCalendarReloadKey((value) => value + 1);
  };

  const handleWidgetDayChange = (nextDay) => {
    const nextParams = new URLSearchParams(searchParams);
    const normalizedDay = normalizeDayValue(nextDay);

    if (!normalizedDay) {
      nextParams.delete('day');
    } else {
      nextParams.set('day', normalizedDay);
    }

    replaceSearchParamsIfChanged(nextParams);
  };

  const handleWidgetGroupChange = (nextGroupKey) => {
    const nextParams = new URLSearchParams(searchParams);
    const normalizedGroupKey = normalizeDayValue(nextGroupKey);

    if (!normalizedGroupKey) {
      nextParams.delete('groupKey');
    } else {
      nextParams.set('groupKey', normalizedGroupKey);
    }

    replaceSearchParamsIfChanged(nextParams);
  };

  const currentDay =
    parsePositiveDay(dayParam) || (maxDay ? String(maxDay) : '');

  const dayOptions = useMemo(() => {
    if (!maxDay) return [];
    return Array.from({ length: maxDay }, (_, index) => {
      const value = index + 1;
      return { value: String(value), label: `Journée ${value}` };
    }).reverse();
  }, [maxDay]);

  const widgetMetaReady = useMemo(() => {
    if (selectedCompetitionIds.length === 0) return true;
    if (competitionInfos.length !== selectedCompetitionIds.length) return false;

    const metaIds = competitionInfos
      .map((item) => String(item.id || '').trim())
      .filter(Boolean)
      .sort();

    const selectedIds = [...selectedCompetitionIds]
      .map((id) => String(id).trim())
      .filter(Boolean)
      .sort();

    return JSON.stringify(metaIds) === JSON.stringify(selectedIds);
  }, [competitionInfos, selectedCompetitionIds]);

  const parsedCurrentDay = parsePositiveDay(dayParam);

  const activeTabNeedsDay =
      activeTab === 'team_standings' && selectedCompetitionIds.length === 1;

  const dayReady =
      !activeTabNeedsDay ||
      Boolean(parsedCurrentDay) ||
      (!metaLoading && maxDay == null);

  const activeTabNeedsSeason =
    activeTab === 'player_ranking' ||
    activeTab === 'team_ranking' ||
    activeTab === 'competition_results';

  const seasonReady =
    !activeTabNeedsSeason ||
    !effectiveDiscipline ||
    seasonMode === 'since' ||
    selectedSeasonsFromUrl.length > 0 ||
    (!loadingSeasons && seasonOptions.length === 0);

  const activeTabNeedsMeta =
    activeTab === 'competition_results';

  const metaReadyForActiveTab =
    !activeTabNeedsMeta || widgetMetaReady;

  const canRenderWidget = activeTabIsDirectCalendar
  ? Boolean(activeWidgetConfigurationId)
  : Boolean(effectiveDiscipline) &&
    Boolean(activeTab) &&
    selectedCompetitionIds.length > 0 &&
    seasonReady &&
    metaReadyForActiveTab &&
    dayReady;

  const showComposedLoader =
    (activeTabNeedsSeason &&
      effectiveDiscipline &&
      selectedCompetitionIds.length > 0 &&
      !seasonReady) ||
    (activeTabNeedsMeta &&
      effectiveDiscipline &&
      selectedCompetitionIds.length > 0 &&
      !metaReadyForActiveTab) ||
    (activeTabNeedsDay && !dayReady) ||
    (metaLoading && activeTab === 'team_standings');


  const renderActiveWidget = () => {
    if (!activeTab) return null;

    switch (activeTab) {
      case 'player_ranking':
        return (
          <PlayerRankingWidget
            discipline={effectiveDiscipline}
            season={widgetSeason}
            widgetConfigurationId={activeWidgetConfigurationId}
            competitionIds={selectedCompetitionIds}
            // title={
            //   selectedCompetitionIds.length > 1
            //     ? 'Statistiques joueurs'
            //     : competitionInfos?.[0]?.name || 'Statistiques joueurs'
            // }
            title=""
            showCompetitionInfos={false}
            statType={statType}
            onStatTypeChange={handleStatTypeChange}
            onNavigate={(url) => navigate(url)}
          />
        );

      case 'team_ranking':
        return (
          <TeamRankingWidget
            discipline={effectiveDiscipline}
            season={widgetSeason}
            widgetConfigurationId={activeWidgetConfigurationId}
            competitionIds={selectedCompetitionIds}
            title=""
            showCompetitionInfos={false}
            statType={statType}
            onStatTypeChange={handleStatTypeChange}
            onNavigate={(url) => navigate(url)}
          />
        );

      case 'team_standings':
        return (
          <StandingsWidget
            discipline={effectiveDiscipline}
            competitionIds={selectedCompetitionIds}
            competitionInfos={competitionInfos}
            widgetConfigurationId={activeWidgetConfigurationId}
            day={selectedCompetitionIds.length === 1 ? normalizeDayValue(dayParam) : null}
            groupKey={groupKeyParam}
            onGroupChange={handleWidgetGroupChange}
            onDayChange={selectedCompetitionIds.length === 1 ? handleWidgetDayChange : undefined}
            title=""
            showCompetitionInfos={false}
            statType={statType}
            onStatTypeChange={handleStatTypeChange}
            onNavigate={(url) => navigate(url)}
          />
        );

      case 'competition_results':
        return (
          <ResultsWidget
            discipline={effectiveDiscipline}
            competitionIds={selectedCompetitionIds}
            competitionInfos={competitionInfos}
            day={selectedCompetitionIds.length === 1 ? normalizeDayValue(dayParam) : null}
            widgetConfigurationId={activeWidgetConfigurationId || null}
            title=""
            showCompetitionInfos={false}
            onNavigate={(url) => navigate(url)}
            onDayChange={selectedCompetitionIds.length === 1 ? handleWidgetDayChange : undefined}
          />
        );

        case 'direct_calendar':
          return (
            <DirectCalendarWidget
              widgetConfigurationId={activeWidgetConfigurationId}
              discipline={effectiveDiscipline || null}
              competitionIds={selectedCalendarCompetitionIds}
              competitionFilterPlacement="portal"
              competitionFilterPortalTarget={calendarCompetitionFilterPortalTarget}
              onCompetitionFilterVisibilityChange={handleCalendarCompetitionFilterVisibilityChange}
              reloadKey={calendarReloadKey}
              onRefresh={handleRefreshCalendar}
              onCompetitionIdsChange={handleCalendarCompetitionIdsChange}
              onNavigate={(url) => navigate(url)}
            />
          );

      default:
        return null;
    }
  };

const showDisciplineInput = allowedDisciplines.length > 1;


const showSharedFilters = !activeTabIsDirectCalendar;


const showSeasonInput =
  showSharedFilters &&
  Boolean(effectiveDiscipline) &&
  seasonOptions.length > 1;

const showCompetitionInput =
  showSharedFilters &&
  Boolean(effectiveDiscipline) &&
  competitions.length > 1;

const showDayInput =
  false &&
  showSharedFilters &&
  showDayFilter &&
  selectedCompetitionIds.length === 1 &&
  (!maxDay || maxDay > 1);

const showFilterPanel =
  showDisciplineInput ||
  showSeasonInput ||
  showCompetitionInput ||
  showDayInput ||
  Boolean(competitionsError) ||
  (activeTabIsDirectCalendar && showCalendarCompetitionFilter);

  return (
    <Box
      className="stats-builder-page stats-builder-page--composed"
      sx={{
        width: '100%',
        maxWidth: '100%',
        boxSizing: 'border-box',
        px: { xs: 1.5, sm: 2, md: 3 },
        py: { xs: 2, md: 3 },
        bgcolor: '#fff',
      }}
    >
      <Box sx={{ maxWidth: 1180, mx: 'auto' }} className="stats-builder-page__container">

         <Stack spacing={2} className="stats-builder-panel__content_inner">
          <Box className="stats-builder__header">
              {widgetConfig?.configName && resolvedComposedTitle &&
              <Box className="stats-builder__title-wrap">
                  <span className="stats-builder__title-dot" aria-hidden="true" />
                    <Typography variant="h5" className="stats-builder__title">
                    {resolvedComposedTitle}
                  </Typography>
               </Box>
              }
            </Box>

              {widgetConfigError ? (
                <Alert severity="error">{widgetConfigError}</Alert>
              ) : null}

              {widgetConfigLoading ? (
                <Box className="stats-builder-loader">
                  <CircularProgress size={24} />
                </Box>
              ) : null}

              {availableTabValues.length > 0 ? (
                <Tabs
                  value={activeTab}
                  onChange={handleTabChange}
                  variant="scrollable"
                  scrollButtons="auto"
                  className="stats-builder-tabs"
                >
                  {childWidgets.map((child) => (
                    <Tab
                      key={child.widgetType}
                      value={child.widgetType}
                      label={TAB_LABELS[child.widgetType] || child.widgetType}
                    />
                  ))}
                </Tabs>
              ) : null}
        </Stack>

        <Stack spacing={3} className="stats-builder-page__stack">

          {showFilterPanel ? (
          <Paper
            elevation={0}
            variant="outlined"
            className="stats-builder-panel"
            sx={{
              borderRadius: 3,
              borderColor: '#e5e7eb',
              p: { xs: 2, md: 3 },
            }}
          >
           
            <Stack spacing={2} className="stats-builder-panel__content_inner">

            
          <Box className="stats-builder-panel__content stats-builder-panel__content--filters">

              {showDisciplineInput ? (
                <>
                <Box className="stats-builder__filter-input">
                  <p>Sport</p>
                  <TextField
                    select
                    fullWidth
                    value={effectiveDiscipline}
                    onChange={handleDisciplineChange}
                  >
                    <MenuItem value="" className="stats-builder-menu-item">
                      <em>Sélectionner un sport</em>
                    </MenuItem>

                    {DISCIPLINE_OPTIONS
                      .filter((option) => allowedDisciplines.includes(option.value))
                      .map((option) => (
                        <MenuItem key={option.value} value={option.value} className="stats-builder-menu-item">
                          {option.label}
                        </MenuItem>
                      ))}
                  </TextField>
                  </Box>

                  {waitingForDisciplineSelection ? (
                    <Alert severity="info">
                      Sélectionnez un sport pour charger les saisons, compétitions et les statistiques.
                    </Alert>
                  ) : null}
                </>
              ) : null}

              {effectiveDiscipline ? (
  <>
    {showSeasonInput && (
      <Box className="stats-builder__filter-input">
                  <p>Saison</p>
                <Autocomplete
                  multiple
                  options={seasonOptions}
                  value={selectedSeasons}
                  loading={loadingSeasons}
                  onChange={handleSeasonsChange}
                  getOptionLabel={(option) => option?.label || ''}
                  isOptionEqualToValue={(option, value) => option.value === value.value}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label=""
                      placeholder="Sélectionner une ou plusieurs saisons"
                    />
                  )}
                />
                </Box>
              )}

              {showCompetitionInput && (
            <Box className="stats-builder__filter-input">
                  <p>Compétitions</p>
                  <Autocomplete
                    multiple
                    disableCloseOnSelect
                    limitTags={5}
                    options={competitions}
                    value={selectedCompetitions}
                    loading={loadingCompetitions}
                    onChange={handleCompetitionsChange}
                    getOptionLabel={(option) =>
                      option?.season ? `${option.name} (${option.season})` : option?.name || ''
                    }
                    isOptionEqualToValue={(option, value) =>
                      String(option._id) === String(value._id)
                    }
                    getOptionDisabled={(option) =>
                      selectedCompetitions.length >= MAX_SELECTED_COMPETITIONS &&
                      !selectedCompetitions.some(
                        (selected) => String(selected._id) === String(option._id)
                      )
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label=""
                        placeholder="Sélectionner jusqu’à 5 compétitions"
                        // helperText={`Maximum ${MAX_SELECTED_COMPETITIONS} compétitions`}
                      />
                    )}
                  />
                  </Box>
              )}

              {showDayInput ? (
                <Box className="stats-builder__filter-input">
                  <p>Journée</p>

                  {maxDay ? (
                    <TextField
                      select
                      fullWidth
                      value={currentDay}
                      onChange={handleDaySelectChange}
                    >
                      {dayOptions.map((option) => (
                        <MenuItem
                          key={option.value}
                          value={option.value}
                          className="stats-builder-menu-item"
                        >
                          {option.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  ) : (
                    <TextField
                      fullWidth
                      value={dayInput}
                      onChange={handleDayChange}
                      onBlur={handleDayBlur}
                      placeholder="Ex. 18"
                      inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                    />
                  )}
                </Box>
              ) : null}

            {competitionsError ? (
                  <Alert severity="error">{competitionsError}</Alert>
                ) : null}
              </>
            ) : null}

              {activeTabIsDirectCalendar ? (
                <Box
                  ref={setCalendarCompetitionFilterPortalTarget}
                  sx={{ display: 'contents' }}
                />
              ) : null}
            </Box>
            </Stack>
          </Paper>
          ) : null}


          {canRenderWidget ? (
              renderActiveWidget()
            ) : showComposedLoader ? (
              <Box className="stats-builder-widget-loader">
                <CircularProgress size={28} />
              </Box>
            ) : null}
             {canRenderWidget ? (
            <Footer isDSAS logoSize="l" align="center" />
          ) : null}
        </Stack>
      </Box>
    </Box>
  );
}